import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import FAQ, { getFAQSchema } from '@/pages/home/components/FAQ';
import { useEffect } from 'react';

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://biffdigital.com';
const pagePath = '/sektorler/e-ticaret-dijital-pazarlama';
const pageUrl = `${siteUrl}${pagePath}`;

const ecommerceFAQItems = [
  {
    question: 'E-ticaret markaları için dijital pazarlama neden önemli?',
    answer:
      'E-ticarette trafik tek başına yeterli değildir. Önemli olan doğru trafiği çekmek, dönüşüm oranını optimize etmek ve müşteri yaşam boyu değerini artırmaktır. Dijital pazarlama, bu üç hedefi aynı anda yönetmenizi sağlar.',
  },
  {
    question: 'E-ticarette en önemli metrik nedir?',
    answer:
      'ROAS (reklam harcamalarının getirisi) önemlidir ancak tek başına yeterli değildir. Müşteri edinme maliyeti (CAC), yaşam boyu değer (LTV), sepet terk oranı ve tekrar satın alma oranı birlikte değerlendirilmelidir. Kârlı büyüme için tüm bu metrikleri birlikte yönetiyoruz.',
  },
  {
    question: 'Sepet terk oranını nasıl düşürürsünüz?',
    answer:
      'Sepet terk oranını düşürmek için checkout akışını basitleştirir, güven sinyallerini güçlendirir, beklenmedik maliyetleri önceden gösterir ve terk eden kullanıcılara akıllı e-posta/remarketing ile geri döneriz. Ortalama sepet terk oranı %70\'tir; biz bu oranı %50-55 bandına çekmeyi hedefleriz.',
  },
  {
    question: 'Hangi e-ticaret platformlarıyla çalışıyorsunuz?',
    answer:
      'Shopify, WooCommerce, Trendyol, Hepsiburada, Amazon ve özel geliştirme platformlarla çalışıyoruz. Platformdan bağımsız olarak stratejiyi markanın ihtiyacına göre kurguluyoruz.',
  },
  {
    question: 'E-ticaret SEO\'su normal SEO\'dan farklı mı?',
    answer:
      'Evet. E-ticaret SEO\'su ürün sayfaları, kategori sayfaları, filtre yapıları ve ürün beslemeleri gibi kendine özgü yapıları yönetmeyi gerektirir. Ayrıca ürün açıklamaları, görsel optimizasyonu ve yapısal veri (Product schema) kritik önemdedir.',
  },
  {
    question: 'E-ihracat için nasıl bir strateji izliyorsunuz?',
    answer:
      'E-ihracatta pazar seçimi, lokalizasyon, lojistik, ödeme yöntemleri ve yerel pazarlama stratejisini birlikte ele alıyoruz. Amazon Global, yerel pazar yerleri ve markanın kendi sitesi üzerinden çok kanallı bir büyüme modeli kuruyoruz.',
  },
];

const faqSchema = getFAQSchema(ecommerceFAQItems);

const pageSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Service',
      name: 'E-Ticaret Dijital Pazarlama Hizmeti',
      provider: {
        '@type': 'Organization',
        name: 'BIFF Digital',
        url: siteUrl,
        telephone: '+905557101968',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Söğütözü Cad. No: 2 Koç Kuleleri C Blok 17',
          addressLocality: 'Çankaya',
          addressRegion: 'Ankara',
          addressCountry: 'TR',
          postalCode: '06530',
        },
      },
      description:
        'E-ticaret markaları için performans pazarlama, SEO, CRO ve dönüşüm optimizasyonuyla satışlarınızı ölçeklendirin. BIFF Digital e-ticaret büyümesini veri ve stratejiyle yönetir.',
      url: pageUrl,
      areaServed: { '@type': 'Country', name: 'Turkey' },
      serviceType: 'E-Ticaret Dijital Pazarlama',
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'E-Ticaret Hizmet Detayları',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Performans Pazarlama', description: 'Google Ads, Meta Ads ve alışveriş reklamları yönetimi' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Ürün SEO', description: 'Ürün sayfaları ve kategori SEO optimizasyonu' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'CRO & Dönüşüm', description: 'Sepet optimizasyonu ve dönüşüm oranı artırma' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'CRM & Retargeting', description: 'Müşteri ilişkileri ve yeniden hedefleme stratejileri' } },
        ],
      },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Sektörler', item: `${siteUrl}/sektorler/e-ticaret-dijital-pazarlama` },
        { '@type': 'ListItem', position: 3, name: 'E-Ticaret', item: pageUrl },
      ],
    },
    {
      '@type': 'SpeakableSpecification',
      cssSelector: ['.speakable-summary'],
    },
    faqSchema,
  ],
};

export default function ETicaret() {
  useEffect(() => {
    document.title = 'E-Ticaret Dijital Pazarlama Ajansı | Satış & Dönüşüm | BIFF Digital';

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', pageUrl);

    return () => {
      link?.setAttribute('href', siteUrl);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />
      <Navbar />
      <main className="pt-16 md:pt-20">

        {/* HERO */}
        <section className="relative w-full overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Cinematic%20e-commerce%20concept%20with%20floating%20shopping%20bags%20and%20product%20boxes%20in%20a%20dark%20premium%20studio%20setting%2C%20dramatic%20crimson%20red%20accent%20lighting%2C%20sleek%20modern%20retail%20atmosphere%2C%20dark%20charcoal%20and%20red%20tones%2C%20premium%20editorial%20product%20photography%2C%20dramatic%20lighting%2C%20no%20people%2C%20wide%20angle&width=1600&height=900&seq=eticaret-hero-01&orientation=landscape"
              alt="E-Ticaret Dijital Pazarlama - Satış ve Dönüşüm Stratejisi"
              title="E-Ticaret Dijital Pazarlama - BIFF Digital"
              className="w-full h-[420px] md:h-[560px] object-cover object-top"
              fetchPriority="high"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30" />
          <div className="relative z-10 w-full h-[420px] md:h-[560px] flex items-center justify-center">
            <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <p className="text-primary-500 font-bold text-sm tracking-widest uppercase mb-4">SEKTÖRLER</p>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white tracking-tighter leading-none mb-6">
                E-Ticaret Markaları<br />İçin Büyüme
              </h1>
              <p className="text-xl md:text-2xl text-white font-medium leading-relaxed mb-6">
                Trafik Kolay.<br />
                <span className="text-primary-500">Satış Zor.</span>
              </p>
              <p className="text-sm md:text-base text-white/80 leading-relaxed max-w-2xl mx-auto speakable-summary">
                Ürünü değil, satın alma yolculuğunu yönetiyoruz. E-ticarette asıl mesele trafik çekmek değil, gelen trafiği satın alan müşteriye dönüştürmektir. Sepete eklemekle iş bitmez.
              </p>
            </div>
          </div>
        </section>

        {/* Ürünü Değil, Satın Alma Yolculuğunu Yönetiyoruz */}
        <section className="section-padding">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter mb-8">
              Ürünü Değil,<br />Satın Alma Yolculuğunu Yönetiyoruz.
            </h2>
            <p className="text-sm md:text-base text-foreground-400 leading-relaxed mb-6">
              Bir kullanıcı mağazanıza gelir, ürünleri inceler, sepete ekler, belki çıkar, belki alır, belki geri döner. Bu yolculuğun her adımında onu anlamak, yönlendirmek ve ikna etmek gerekir. İşte bizim yaptığımız tam olarak bu.
            </p>
            <div className="glass-card rounded-2xl p-6 md:p-8 border-l-4 border-primary-500 mb-8">
              <p className="text-base md:text-lg text-foreground-50 font-bold leading-relaxed">
                Sepete eklemekle iş bitmez.
              </p>
              <p className="text-sm text-foreground-300 mt-3 leading-relaxed">
                Asıl mesele, o sepetin ödeme sayfasına ulaşmasıdır.
              </p>
            </div>
          </div>
        </section>

        {/* Ne Yapıyoruz */}
        <section className="section-padding bg-background-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 md:mb-14">
              <p className="text-primary-500 font-bold text-sm tracking-widest uppercase mb-2">NE YAPIYORUZ?</p>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter">
                Trafikten Satışa<br />Tam Dönüşüm Yönetimi
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-5 md:gap-6">
              {[
                { icon: 'ri-bar-chart-line', title: 'Performans Pazarlama', desc: 'Google Ads, Meta Ads ve alışveriş reklamlarını ROAS odaklı yönetiyoruz. Ürün bazlı kampanya yapıları ve dinamik remarketing\'le reklam bütçenizi en verimli şekilde kullanıyoruz.' },
                { icon: 'ri-search-line', title: 'Ürün SEO', desc: 'Ürün sayfaları, kategori sayfaları ve ürün beslemelerini arama motorları için optimize ediyoruz. Organik trafiği sürdürülebilir bir satış kanalına dönüştürüyoruz.' },
                { icon: 'ri-line-chart-line', title: 'CRO & Dönüşüm Optimizasyonu', desc: 'Mevcut trafiğinizin neden satın almadığını analiz ediyoruz. Checkout akışı, güven sinyalleri, fiyat gösterimi ve mobil deneyimi test ederek dönüşüm oranını artırıyoruz.' },
                { icon: 'ri-refresh-line', title: 'Retargeting', desc: 'Sepeti terk eden, ürünü inceleyip almayan veya belirli bir kategoride gezinen kullanıcılara kişiselleştirilmiş remarketing kampanyalarıyla geri dönüyoruz.' },
                { icon: 'ri-customer-service-2-line', title: 'CRM & Müşteri Yolculuğu', desc: 'Tek seferlik satıştan tekrar eden müşteri ilişkisine geçiş. E-posta otomasyonları, segmentasyon ve kişiselleştirilmiş tekliflerle LTV\'yi artırıyoruz.' },
                { icon: 'ri-filter-3-line', title: 'Satış Hunisi Analizi', desc: 'Kullanıcının mağazaya girdiği andan satın almaya kadar geçtiği her adımı izliyor, kırılma noktalarını tespit ediyor ve huniyi sürekli optimize ediyoruz.' },
              ].map((item, i) => (
                <div key={i} className="glass-card-hover rounded-2xl p-6 md:p-8 group">
                  <div className="w-12 h-12 bg-background-200 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary-500/10 transition-colors duration-300">
                    <i className={`${item.icon} text-xl text-primary-500`} />
                  </div>
                  <h3 className="text-lg font-bold text-foreground-50 mb-3">{item.title}</h3>
                  <p className="text-sm text-foreground-400 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Growth Audit */}
        <section className="section-padding">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter mb-8">
              Growth Audit
            </h2>
            <p className="text-sm md:text-base text-foreground-400 leading-relaxed mb-8">
              E-ticaret markanızın mevcut durumunu 5 boyutta analiz ediyoruz:
            </p>
            <div className="space-y-4">
              {[
                'Trafik kaynakları ve maliyetleri',
                'Dönüşüm hunisi ve kırılma noktaları',
                'Ürün sayfalarının ikna gücü',
                'Sepet ve checkout deneyimi',
                'Müşteri elde tutma ve LTV stratejisi',
              ].map((item, i) => (
                <div key={i} className="glass-card rounded-2xl p-5 flex items-center gap-4">
                  <span className="w-8 h-8 rounded-lg bg-primary-500/10 flex items-center justify-center shrink-0">
                    <span className="text-xs font-black text-primary-500">{`0${i + 1}`}</span>
                  </span>
                  <span className="text-sm font-medium text-foreground-200">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <FAQ
          items={ecommerceFAQItems}
          sectionTitle="SIK SORULANLAR"
          sectionSubtitle="E-TİCARET"
          sectionSummary="E-ticaret dijital pazarlama hakkında en sık sorulan soruları, doğrudan ve net cevaplarla burada topladık."
          speakableClass={false}
        />

        {/* CTA */}
        <section className="section-padding bg-background-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="glass-card rounded-2xl p-8 md:p-12 text-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/5 rounded-bl-full -mr-16 -mt-16" />
              <div className="relative z-10">
                <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-50 tracking-tighter mb-4">
                  Mağazanızın nerede para kaybettiğini<br />
                  <span className="text-primary-500">birlikte bulalım.</span>
                </h2>
                <p className="text-sm md:text-base text-foreground-400 mb-8 max-w-xl mx-auto leading-relaxed">
                  Ücretsiz Growth Audit ile mağazanızın dönüşüm hunisini analiz edelim. Nerede müşteri kaybediyorsunuz, hangi fırsatları kaçırıyorsunuz — veriyle görelim.
                </p>
                <a href="/iletisim" className="btn-primary text-base px-8 py-4">
                  Ücretsiz Growth Audit
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}